/**
 * DecreeSuggestControls.tsx — "Podpowiedz dekret" + "Zapisz jako szablon", renderowane
 * OBOK `DecreeLinesEditor` na zakładce Dekretacja obu klas faktur (Purchase/Sale). Te same
 * warunki widoczności co sam edytor (`editing && canManageAccounting`) — rodzic
 * (PurchaseInvoiceDetail/SaleInvoiceDetail) już to bramkuje przed zamontowaniem tego
 * komponentu.
 *
 * "Podpowiedz dekret": picker aktywnych szablonów firmy+klasy (`GET /decree-templates`) +
 * przycisk wołający `POST /decree-templates/suggest` — wynik WYPEŁNIA draft
 * (`onApplySuggestion`, na stronie faktury mapuje się na `handleDecreeChange` — oznacza
 * dirty). NIC nie jest zapisywane tutaj — zapis idzie WYŁĄCZNIE przez zwykły zapis faktury
 * (CLAUDE.md §4.14, atomowość nietknięta, ten komponent nie otwiera żadnej transakcji).
 *
 * MOSTEK do nowego kształtu wiersza (uproszczenie modelu dekretacji — `document_decree_lines`
 * ma dziś `mainAccount`+`subAccount1..5` zamiast `accountId`/`accountNumber`): backend
 * `decree-templates` NIE jest przebudowywany (nietknięty, świadomie poza zakresem — nadal
 * operuje na `accountNumber` string, bez wymuszonego formatu/separatora segmentów). Sugerowana
 * linia dostaje więc cały `accountNumber` wprost do `mainAccount`, `subAccount1..5` zostają
 * puste — nie da się bezpiecznie rozbić dowolnego stringa na segmenty, user doprecyzuje ręcznie
 * w edytorze. Odwrotnie przy "Zapisz jako szablon": `mainAccount` drafta jedzie jako
 * `accountNumber` szablonu (kontrakt `decree_templates.lines` się nie zmienił).
 *
 * "Zapisz jako szablon": modal z nazwą (custom `<Modal>`, NIE window.prompt — CLAUDE.md
 * §4.12) → `POST /decree-templates/save-as-template` z BIEŻĄCYMI wierszami draftu.
 * Wyłączony gdy któryś wiersz nie ma jeszcze wypełnionego konta głównego (`mainAccount` puste) —
 * backend i tak by odrzucił takim 400, front zapobiega zamiast łapać błąd.
 *
 * Obie akcje wymagają zapisanego dokumentu (`documentId`) — na nowej niezapisanej fakturze
 * `documents.amount_net/vat/gross` (potrzebne suggest do materializacji, save-as-template do
 * heurystyki `amountSource`) jeszcze nie istnieją w bazie.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Save, Sparkles } from 'lucide-react';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { type DecreeLineDraft } from '@/lib/documents/decree-lines';

export type DecreeSuggestDocumentClass = 'purchase_invoice' | 'sale_invoice';

interface DecreeTemplateSummary {
  id: string;
  name: string;
}

interface DecreeTemplatesListResponse {
  templates: DecreeTemplateSummary[];
}

interface SuggestedDecreeLine {
  groupNo: number;
  lineNumber: number;
  side: 'debit' | 'credit';
  accountId: string;
  accountNumber: string;
  amount: string;
  description?: string;
}

interface SuggestResponse {
  lines: SuggestedDecreeLine[];
}

export interface DecreeSuggestControlsProps {
  /** `null`/`undefined` gdy dokument jeszcze nie zapisany — obie akcje wyłączone. */
  documentId: string | null | undefined;
  companyId: string | null | undefined;
  documentClass: DecreeSuggestDocumentClass;
  /** Bieżący draft edytora (`decreeDraft` na stronie faktury) — źródło dla "Zapisz jako szablon". */
  currentLines: DecreeLineDraft[];
  onApplySuggestion: (lines: DecreeLineDraft[]) => void;
  'data-testid'?: string;
}

function errorBody(err: unknown): Record<string, unknown> | null {
  if (isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
    return err.response.data as Record<string, unknown>;
  }
  return null;
}

export function DecreeSuggestControls({
  documentId,
  companyId,
  documentClass,
  currentLines,
  onApplySuggestion,
  'data-testid': testId = 'decree-suggest-controls',
}: DecreeSuggestControlsProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // QueryKey prefix ['decree-templates'] dzielony z DecreeTemplatesTab (Ustawienia) —
  // mutacja tam invaliduje ['decree-templates', 'picker'] wprost, więc lista tutaj odświeża
  // się automatycznie po edycji szablonów bez ręcznej koordynacji między dwoma miejscami.
  const templatesQ = useQuery({
    queryKey: ['decree-templates', 'picker', companyId, documentClass],
    queryFn: async () => {
      const params = new URLSearchParams({ documentClass, active: 'true' });
      if (companyId) params.set('companyId', companyId);
      const res = await api.get<DecreeTemplatesListResponse>(
        `/decree-templates?${params.toString()}`,
      );
      return res.data;
    },
    enabled: !!companyId,
    staleTime: 30_000,
  });
  const templates = templatesQ.data?.templates ?? [];

  function mapSuggestErrorTitle(err: unknown): string {
    const body = errorBody(err);
    const code = typeof body?.error === 'string' ? body.error : undefined;
    if (code === 'DECREE_SUGGEST_ACCOUNT_INVALID') {
      const accountNumber = typeof body?.accountNumber === 'string' ? body.accountNumber : '?';
      return t('tenant.documents.decree.suggest.toast.accountInvalid', { accountNumber });
    }
    if (code === 'DECREE_SUGGEST_AMOUNT_MISSING') {
      const rawSource = typeof body?.amountSource === 'string' ? body.amountSource : '?';
      const amountSource = t(`tenant.documents.decree.templates.amountSources.${rawSource}`, {
        defaultValue: rawSource,
      });
      return t('tenant.documents.decree.suggest.toast.amountMissing', { amountSource });
    }
    return t('tenant.documents.decree.suggest.toast.genericError');
  }

  const suggestMutation = useMutation({
    mutationFn: async () => {
      if (!documentId || !selectedTemplateId) throw new Error('NOT_READY');
      const res = await api.post<SuggestResponse>('/decree-templates/suggest', {
        documentId,
        templateId: selectedTemplateId,
      });
      return res.data.lines;
    },
    onSuccess: (lines) => {
      onApplySuggestion(
        lines.map((line) => ({
          key: crypto.randomUUID(),
          groupNo: line.groupNo,
          side: line.side,
          mainAccount: line.accountNumber,
          amount: line.amount,
          description: line.description,
        })),
      );
      toast.show({ variant: 'success', title: t('tenant.documents.decree.suggest.toast.applied') });
    },
    onError: (err) => {
      toast.show({ variant: 'error', title: mapSuggestErrorTitle(err) });
    },
  });

  const saveAsTemplateMutation = useMutation({
    mutationFn: async () => {
      if (!documentId) throw new Error('NOT_READY');
      await api.post('/decree-templates/save-as-template', {
        documentId,
        name: templateName.trim(),
        lines: currentLines.map((line) => ({
          groupNo: line.groupNo,
          side: line.side,
          accountNumber: line.mainAccount,
          amount: line.amount,
          description: line.description?.trim() || undefined,
        })),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['decree-templates'] });
      toast.show({
        variant: 'success',
        title: t('tenant.documents.decree.suggest.saveAsTemplate.toast.success'),
      });
      setSaveModalOpen(false);
      setTemplateName('');
    },
    onError: (err) => {
      const body = errorBody(err);
      const code = typeof body?.error === 'string' ? body.error : undefined;
      const title =
        code === 'TEMPLATE_NAME_TAKEN'
          ? t('tenant.documents.decree.suggest.saveAsTemplate.toast.nameTaken')
          : t('tenant.documents.decree.suggest.saveAsTemplate.toast.genericError');
      toast.show({ variant: 'error', title });
    },
  });

  const canSuggest = !!documentId && !!selectedTemplateId && !suggestMutation.isPending;
  const allLinesHaveAccount = currentLines.every((line) => line.mainAccount.trim() !== '');
  const canOpenSaveModal = !!documentId && currentLines.length > 0 && allLinesHaveAccount;

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
      <Dropdown
        options={[
          {
            value: '',
            label: t('tenant.documents.decree.suggest.templatePickerPlaceholder'),
          },
          ...templates.map((tpl) => ({ value: tpl.id, label: tpl.name })),
        ]}
        value={selectedTemplateId}
        onChange={(v) => setSelectedTemplateId(v)}
        disabled={!documentId || templatesQ.isLoading}
        aria-label={t('tenant.documents.decree.suggest.templatePickerLabel')}
        className="w-56"
        data-testid={`${testId}-template-picker`}
      />
      <Button
        variant="secondary"
        size="sm"
        onClick={() => suggestMutation.mutate()}
        disabled={!canSuggest}
        loading={suggestMutation.isPending}
        data-testid={`${testId}-apply-btn`}
      >
        <Sparkles className="mr-1 h-3.5 w-3.5" />
        {t('tenant.documents.decree.suggest.applyButton')}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setSaveModalOpen(true)}
        disabled={!canOpenSaveModal}
        data-testid={`${testId}-save-as-template-btn`}
      >
        <Save className="mr-1 h-3.5 w-3.5" />
        {t('tenant.documents.decree.suggest.saveAsTemplate.button')}
      </Button>

      <Modal
        open={saveModalOpen}
        onClose={() => setSaveModalOpen(false)}
        title={t('tenant.documents.decree.suggest.saveAsTemplate.modalTitle')}
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setSaveModalOpen(false)}
              disabled={saveAsTemplateMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => saveAsTemplateMutation.mutate()}
              disabled={!templateName.trim() || saveAsTemplateMutation.isPending}
              loading={saveAsTemplateMutation.isPending}
              data-testid={`${testId}-save-as-template-confirm`}
            >
              {t('tenant.documents.decree.suggest.saveAsTemplate.confirm')}
            </Button>
          </>
        }
      >
        <label className="mb-1 block text-xs font-medium text-text-secondary">
          {t('tenant.documents.decree.suggest.saveAsTemplate.nameLabel')}
        </label>
        <Input
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          maxLength={200}
          placeholder={t('tenant.documents.decree.suggest.saveAsTemplate.namePlaceholder')}
          data-testid={`${testId}-save-as-template-name`}
        />
        <div
          role="alert"
          className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300"
          data-testid={`${testId}-save-as-template-account-warning`}
        >
          {t('tenant.documents.decree.suggest.saveAsTemplate.accountWarning')}
        </div>
      </Modal>
    </div>
  );
}
